/**
 * Razčlenjevalnik uporabnikovega vnosa zneska (T1143–T1150, preseljen T1796).
 *
 * **Zakaj v `lib/` in ne več v `hooks/useOrgFormats.ts`.** Pravilo je čista
 * funkcija in ga potrebujejo tudi moduli, ki niso komponente — najprej
 * `lib/scenarioAmount.ts` (goal seek). Uvoz iz datoteke s kavlji bi tja
 * potegnil zustand trgovine in React, čeprav gre za razčlembo niza.
 * `useOrgFormats` isto funkcijo izvaža naprej, zato obstoječi uvozi držijo.
 */

/** Zapis decimalnega ločila, kot ga nastavi organizacija. */
export type DecimalFormat = 'dot' | 'comma';

/**
 * Razčleni znesek, kot ga je vnesel uporabnik, v obliko za API (`1234.56`).
 *
 * **Zrcali `apps/boq_builder/importers/decimal_parser.py`**, ki je bil utrjen
 * na resničnem korpusu 21 preglednic (T1143–T1150). Pravilo je isto: o
 * decimalnem ločilu odloči **oblika same vrednosti**, format organizacije pa
 * je le razsodnik pri dvoumnem primeru »en separator in tri mesta za njim«
 * (`1.234` je lahko tisočica ali decimalka). Zaradi tega organizacija na
 * `comma` brez težav vnese tudi `1200.50`.
 *
 * Namerno odstopanje od zaledja: neveljaven vnos vrne `null` in ne nič.
 * Obrazec mora povedati »to ni znesek«, ne tiho poslati `0`.
 */
export function parseMoneyInput(
  raw: string,
  decimalFormat: DecimalFormat,
): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;

  let sign = '';
  let body = trimmed;
  if (body.startsWith('-')) {
    sign = '-';
    body = body.slice(1).trim();
  }

  const hasSpace = /\s/.test(body);
  // Notranji presledki so vedno le ločilo tisočic.
  const s = body.replace(/\s/g, '');
  if (!s || /[^0-9.,]/.test(s)) return null;

  const hasDot = s.includes('.');
  const hasComma = s.includes(',');

  let normalized: string;

  if (hasDot && hasComma) {
    // Ločilo, ki stoji ZADNJE, je decimalno.
    normalized =
      s.lastIndexOf(',') > s.lastIndexOf('.')
        ? s.replace(/\./g, '').replace(',', '.') // evropsko
        : s.replace(/,/g, ''); // ameriško
  } else {
    const sep = hasDot ? '.' : hasComma ? ',' : null;
    if (sep === null) {
      normalized = s;
    } else {
      const parts = s.split(sep);
      if (hasSpace && parts.length === 2) {
        // Tisočice s presledki + en separator → ta separator je decimalen.
        normalized = parts.join('.');
      } else if (parts.length > 2) {
        // Ponovljen separator je lahko samo skupinjenje tisočic (1.000.000).
        normalized = parts.join('');
      } else {
        const [intPart, fracPart] = parts;
        if (fracPart.length === 3 && intPart.length >= 1) {
          // Dvoumno: razsodi format organizacije.
          const decimalSepForHint = decimalFormat === 'dot' ? '.' : ',';
          normalized =
            decimalSepForHint === sep ? parts.join('.') : parts.join('');
        } else {
          normalized = parts.join('.');
        }
      }
    }
  }

  if (!/^\d*\.?\d*$/.test(normalized) || normalized === '.') return null;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return null;

  return sign + normalized;
}

/**
 * Vsota zneskov v **centih** (T2358 f).
 *
 * Seštevanje `Number` nizov s plavajočo vejico razhaja pri večjem številu
 * vrstic (`0.1 + 0.2 = 0.30000000000000004`); vsak znesek zato najprej gre v
 * cele cente, seštejemo cela števila in šele rezultat delimo s 100. Neveljavni
 * ali prazni vnosi štejejo za 0 (kot dotlej `Number(x ?? 0)`).
 */
export function sumMoney(values: ReadonlyArray<string | number | null | undefined>): number {
  const cents = values.reduce<number>((sum, value) => {
    const amount = Number(value ?? 0);
    return sum + (Number.isFinite(amount) ? Math.round(amount * 100) : 0);
  }, 0);
  return cents / 100;
}
